import Grid from '@mui/material/Grid';
import { Theme } from '@mui/material/styles';
import { alpha, Box, styled, Typography, useTheme } from '@mui/material';
import { DateTime } from 'luxon';
import { FlexPoolOnlyIndicator } from 'shared/components/common/flexpool-only-indicator';
import { useMemo, useRef, useState } from 'react';
import { useOgpCompanyTypeContext } from 'ogp/limited-company-capabilities';
import { useOgpGetShiftPopoverDetail } from 'shared/components/shift-forms/hooks/ogp/use-ogp-get-shift-popover-details';
import { useSiteDetection } from 'shared/hooks';
import { getFlexWorkerLabel } from 'shared/utils/get-flex-worker-label';
import { ShiftPopover } from './shift-details-popper';
import { usePlanningParamsContext } from '../../../../providers/planning-params-provider';
import { CalendarPlanningShift } from '../calendar-planning-shift.types';
import { useMultiDayShiftHover } from '../../providers/multi-day-shift-hover-provider';
import { getMultiDayShiftAccentColor } from '../../helpers/get-multi-day-shift-accent-color';

type ShiftData = {
  shiftData: CalendarPlanningShift;
};

const SHIFT_CARD_BORDER_RADIUS = '1rem';
const MULTI_DAY_SHIFT_LEFT_STROKE_WIDTH_PX = 4;
const MULTI_DAY_SHIFT_LEFT_STROKE_DEFAULT_OPACITY = 0.5;
const MULTI_DAY_SHIFT_LEFT_STROKE_HIGHLIGHTED_OPACITY = 1;

const defaultCapacityBoxColor = '#F5FAFF';
const debounceDelay = 500;
const multiDayShiftCardShadow =
  '0px 4px 6px 0px rgba(51, 51, 51, 0.03), 0px 0px 8px 0px rgba(51, 51, 51, 0.03), 0px 12px 16px 0px rgba(51, 51, 51, 0.08)';
const multiDayShiftHighlightedShadow =
  '0px 12px 16px rgba(51, 51, 51, 0.16), 0px 4px 6px rgba(51, 51, 51, 0.08)';

export const CalendarShiftItem = ({ shiftData }: ShiftData) => {
  const theme = useTheme();
  const { site } = useSiteDetection();
  const { hoveredMultiDayShiftId, setHoveredMultiDayShiftId } = useMultiDayShiftHover();

  const { setFilters } = usePlanningParamsContext();
  const { isLimitedCompany } = useOgpCompanyTypeContext();
  const colorData = useMemo(() => getColor({ shiftData }, theme), [shiftData, theme]);

  const [shouldFetchDetails, setShouldFetchDetails] = useState(false);
  const { data, isSuccess } = useOgpGetShiftPopoverDetail(site, shiftData.id, shouldFetchDetails);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isHoveredRef = useRef<boolean>(false);

  const multiDayShiftId = shiftData.multiDayShiftId;
  const isMultiDayShift = multiDayShiftId != null;
  const isGroupHighlighted = isMultiDayShift && hoveredMultiDayShiftId === multiDayShiftId;
  const accentColor = useMemo(
    () =>
      multiDayShiftId != null ? getMultiDayShiftAccentColor(multiDayShiftId, theme) : undefined,
    [multiDayShiftId, theme]
  );
  const popoverData = useMemo(() => {
    if (!isSuccess || !data) {
      return null;
    }

    return {
      id: data.id,
      name: data.name,
      maxClaimants: data.maxClaimants,
      acceptedClaimantsCount: data.claimants.length,
      flexpoolClaimantsCount: data.flexPools.length,
      multiDayShiftDateLabels: data.multiDayShiftDateLabels,
      claimants: data.claimants.map((claimant) => ({
        ...claimant,
        employmentType: isLimitedCompany ? '' : claimant.employmentType,
        label:
          getFlexWorkerLabel(
            isLimitedCompany
              ? {}
              : {
                  employmentType: claimant.label.workerType,
                }
          ) ?? '',
        imageUrl: claimant.imageUrl ?? undefined,
      })),
    };
  }, [isLimitedCompany, isSuccess, data]);

  const startHoverEffect = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    isHoveredRef.current = true;
    const target = event.currentTarget;
    hoverTimerRef.current = setTimeout(() => {
      if (isHoveredRef.current) {
        setShouldFetchDetails(true);
        setAnchorEl(target);
      }
    }, debounceDelay);
  };

  const stopHoverEffect = () => {
    isHoveredRef.current = false;
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    setShouldFetchDetails(false);
    setAnchorEl(null);
  };

  const handleMouseEnter = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (multiDayShiftId != null) {
      setHoveredMultiDayShiftId(multiDayShiftId);
    }
    startHoverEffect(event);
  };

  const handleMouseLeave = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (multiDayShiftId != null) {
      const relatedTarget = event.relatedTarget;
      const isMovingWithinGroup =
        relatedTarget instanceof Element &&
        relatedTarget.closest(`[data-multi-day-shift-id="${multiDayShiftId}"]`) != null;

      if (!isMovingWithinGroup) {
        setHoveredMultiDayShiftId(null);
      }
    }
    stopHoverEffect();
  };

  if (!shiftData) {
    return <EmptyShift item />;
  }
  const isHistorical = DateTime.fromISO(shiftData.dateTime) < DateTime.now();
  const ShiftWrapperComponent = isHistorical ? HistoricalShiftWrapper : RegularShiftWrapper;

  return (
    <ShiftWrapperComponent data-testid={`shift-${shiftData.id}`} item>
      <ShiftBox
        onClick={() => {
          setFilters({
            shiftId: shiftData.id,
          });
        }}
        backgroundColor={colorData.cardColor}
        data-testid={`shift-box-${shiftData.id}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        data-multi-day-shift-id={multiDayShiftId}
        $isMultiDayShift={isMultiDayShift}
        $isGroupHighlighted={isGroupHighlighted}
        $accentColor={accentColor}
      >
        <ShiftJobTitleBox>
          <JobNameTypography variant="subtitle2" noWrap>
            {shiftData.jobName}
          </JobNameTypography>
        </ShiftJobTitleBox>

        <ShiftTimeBox>
          <ShiftTimeTypography variant="subtitle2">
            {shiftData.startTime} - {shiftData.endTime}
          </ShiftTimeTypography>
          <FlexPoolOnlyIndicator flexPoolOnly={shiftData.flexPoolOnly} />
        </ShiftTimeBox>
        <CapacityBox
          backgroundColor={colorData.capacityBoxColor}
          data-testid={`capacity-box-${shiftData.id}`}
        >
          <ClaimantsTypography variant="body2">
            <ClaimantsValue>{shiftData.claimants}</ClaimantsValue>
            {shiftData.hasCanceledClaim ? (
              <RedDot component="span" data-testid={`claimants-red-dot-${shiftData.id}`}></RedDot>
            ) : null}
            <MaxClaimants>
              {' / '}
              {shiftData.maxClaimants}
            </MaxClaimants>
          </ClaimantsTypography>
          <BadgeBox
            backgroundColor={colorData.badgeColor}
            data-testid={`shift-badge-${shiftData.id}`}
          >
            <Typography variant="body2">{shiftData.applicants}</Typography>
          </BadgeBox>
        </CapacityBox>
      </ShiftBox>
      <ShiftPopover
        open={shouldFetchDetails && isSuccess && !!data}
        anchorEl={anchorEl}
        data={popoverData}
        maxClaimants={data?.maxClaimants}
        hideEmploymentType={isLimitedCompany}
      />
    </ShiftWrapperComponent>
  );
};

const shiftWrapperStyle = (theme: Theme, defaultOpacity: number) => ({
  marginBottom: theme.spacing(2),
  opacity: defaultOpacity,
  transition: 'opacity 0.5s',
});

const EmptyShift = styled(Grid)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  backgroundColor: 'transparent',
  height: theme.spacing(4),
}));

export const CalendarShiftRowPlaceholder = styled(Grid)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  height: '10rem',
  flexShrink: 0,
}));

const RegularShiftWrapper = styled(Grid)(({ theme }) => shiftWrapperStyle(theme, 1));

const HistoricalShiftWrapper = styled(Grid)(({ theme }) => shiftWrapperStyle(theme, 0.5));

const ShiftBox = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'backgroundColor' &&
    prop !== '$isMultiDayShift' &&
    prop !== '$isGroupHighlighted' &&
    prop !== '$accentColor',
})<{
  backgroundColor: string;
  $isMultiDayShift: boolean;
  $isGroupHighlighted: boolean;
  $accentColor?: string;
}>(({ theme, backgroundColor, $isMultiDayShift, $isGroupHighlighted, $accentColor }) => {
  const leftStrokeOpacity = $isGroupHighlighted
    ? MULTI_DAY_SHIFT_LEFT_STROKE_HIGHLIGHTED_OPACITY
    : MULTI_DAY_SHIFT_LEFT_STROKE_DEFAULT_OPACITY;
  const multiDayStrokeColor =
    $isMultiDayShift && $accentColor != null ? alpha($accentColor, leftStrokeOpacity) : undefined;

  return {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    backgroundColor,
    padding: theme.spacing(2),
    width: '100%',
    height: '10rem',
    flexShrink: 0,
    borderRadius: SHIFT_CARD_BORDER_RADIUS,
    boxSizing: 'border-box',
    overflow: 'hidden',
    position: 'relative',
    isolation: 'isolate',
    zIndex: $isGroupHighlighted ? 1 : 0,
    boxShadow: $isGroupHighlighted ? multiDayShiftHighlightedShadow : multiDayShiftCardShadow,
    transition: 'box-shadow 0.5s',
    ...(multiDayStrokeColor != null && {
      '&::before': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: MULTI_DAY_SHIFT_LEFT_STROKE_WIDTH_PX,
        backgroundColor: multiDayStrokeColor,
        borderTopLeftRadius: SHIFT_CARD_BORDER_RADIUS,
        borderBottomLeftRadius: SHIFT_CARD_BORDER_RADIUS,
        transition: 'background-color 0.5s',
        pointerEvents: 'none',
        zIndex: 0,
      },
    }),
    '& > *': {
      position: 'relative',
      zIndex: 1,
    },
    ...(!$isMultiDayShift && {
      '&:hover': {
        boxShadow: multiDayShiftHighlightedShadow,
      },
    }),
  };
});

const CapacityBox = styled(Box)<{ backgroundColor: string }>(({ backgroundColor }) => ({
  backgroundColor,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexDirection: 'row',
  borderRadius: '0.75rem',
  padding: '1rem 0.75rem',
}));

const ShiftJobTitleBox = styled(Box)({
  padddingTop: '0.5rem',
  paddingBottom: '0.5rem',
  display: 'flex',
  justifyContent: 'flex-start',
});

const ShiftTimeBox = styled(Box)({
  display: 'flex',
  height: '2rem',
  flexDirection: 'row',
  justifyContent: 'flex-start',
  paddingBottom: '0.5rem',
  alignItems: 'center',
});

const JobNameTypography = styled(Typography)(({ theme }) => ({
  width: '100%',
  fontWeight: 600,
  fontSize: '14px',
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),

  textOverflow: 'ellipsis',
  whiteSpace: 'normal',
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
}));

const ShiftTimeTypography = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  fontSize: '14px',
  letterSpacing: '.3px',
  gap: theme.spacing(2),
  whiteSpace: 'wrap',
  overflow: 'hidden',
}));

const ClaimantsTypography = styled(Typography)({
  fontWeight: 700,
  fontSize: '14px',
  display: 'flex',
});

const ClaimantsValue = styled(Box)(({ theme }) => ({
  color: theme.palette.lwBlack['100'],
}));

const MaxClaimants = styled(Box)(({ theme }) => ({
  color: theme.palette.lwBlack['60'],
}));

const BadgeBox = styled(Box)<{ backgroundColor: string }>(({ theme, backgroundColor }) => ({
  height: theme.spacing(6),
  width: theme.spacing(6),
  backgroundColor,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  paddingTop: '1px',
  '& .MuiTypography-root': {
    color: 'white',
  },
}));

const RedDot = styled(Box)(({ theme }) => ({
  borderRadius: '50%',
  height: '6px',
  width: '6px',
  backgroundColor: theme.palette.lwDanger['100'],
  display: 'inline-block',
  verticalAlign: 'super',
}));

const getColor = ({ shiftData }: Pick<ShiftData, 'shiftData'>, theme: Theme) => {
  if (shiftData) {
    if (DateTime.fromISO(shiftData.dateTime) < DateTime.now()) {
      return {
        cardColor: theme.palette.lwBlack['20'],
        capacityBoxColor: defaultCapacityBoxColor,
        badgeColor: theme.palette.lwBlack['60'],
      };
    }
    if (shiftData.claimants === shiftData.maxClaimants) {
      return {
        cardColor: theme.palette.lwWhite['100'],
        capacityBoxColor: theme.palette.lwSuccess['20'],
        badgeColor: theme.palette.lwSuccess['100'],
      };
    }
    if (shiftData.applicants === 0) {
      return {
        cardColor: theme.palette.lwWhite['100'],
        capacityBoxColor: defaultCapacityBoxColor,
        badgeColor: theme.palette.lwDanger['60'],
      };
    }
    if (shiftData.applicants > 0) {
      return {
        cardColor: theme.palette.lwWhite['100'],
        capacityBoxColor: theme.palette.lwPrimaryAccentLight['10'],
        badgeColor: theme.palette.lwPrimaryAccentLight['100'],
      };
    }
  }
  return {
    cardColor: theme.palette.lwWhite['100'],
    capacityBoxColor: defaultCapacityBoxColor,
    badgeColor: theme.palette.lwBlack['100'],
  };
};
